/**
 * Admin Route — sign in at /auth; access via KV/JWT role, built-in owner emails, or legacy admin session.
 */

import { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { useAdminRole } from '../lib/use-admin-role';
import { useLegacyAdminSessionValid } from '../lib/use-legacy-admin-session';
import { setAdminAccessTokenForApi } from '../lib/db-storage';
import { LoadingFallback } from './LoadingFallback';
import { toast } from 'sonner';

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const location = useLocation();
  const { user, accessToken, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useAdminRole();
  const { valid: legacySessionValid, loading: sessionLoading } = useLegacyAdminSessionValid();
  const deniedToastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (isAdmin && accessToken) {
      setAdminAccessTokenForApi(accessToken);
      return () => setAdminAccessTokenForApi(null);
    }
    if (legacySessionValid) {
      setAdminAccessTokenForApi(null);
    }
  }, [isAdmin, accessToken, legacySessionValid]);

  useEffect(() => {
    // Never treat a raw stored admin key as authenticated route access.
    // Access is granted only via validated admin role or validated admin session.
    if (typeof window === 'undefined') return;
    if (legacySessionValid || isAdmin) return;
    try {
      sessionStorage.removeItem('tension_admin_key');
      localStorage.removeItem('tension_admin_key');
    } catch {
      // Ignore storage failures in restricted contexts.
    }
  }, [legacySessionValid, isAdmin]);

  if (authLoading || roleLoading || sessionLoading) {
    return <LoadingFallback fullScreen />;
  }

  if (legacySessionValid) {
    return <>{children}</>;
  }

  if (!user) {
    return (
      <Navigate
        to="/auth"
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (!isAdmin) {
    const toastKey = `${user.id}:${location.pathname}`;
    if (deniedToastKeyRef.current !== toastKey) {
      deniedToastKeyRef.current = toastKey;
      toast.error('Access denied', {
        description: 'Admin role check failed. Continue at /admin/secure-login with your admin credential.',
      });
    }
    return (
      <Navigate
        to="/admin/secure-login"
        replace
        state={{ from: location.pathname, reason: 'admin_role_unavailable' }}
      />
    );
  }

  return <>{children}</>;
}
